import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import MarketSectionPage from '../Components/Marketplace/MarketSectionPage';
import HomeCartSummary from '../Components/Cart/HomeCartSummary';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

/**
 * Layout marketplace con keep-alive + slide horizontal.
 *
 * Arquitectura:
 *
 *   ┌─────────────────────────────────┐  ← viewport (slider area = full)
 *   │  ┌──────────┐ ┌──────────┐      │
 *   │  │ Food     │ │ Grocery  │      │   Slide horizontal 400ms entre las dos.
 *   │  │ (active) │ │ (offscreen)     │   Cada una con su PROPIO scroll.
 *   │  │ Header + │ │ Header + │      │
 *   │  │ Banner + │ │ Banner + │      │   Cada sección renderiza SU propio
 *   │  │ Content  │ │ Content  │      │   header/banner inline — pueden ser
 *   │  │          │ │          │      │   diferentes (color, animación, etc).
 *   │  └──────────┘ └──────────┘      │
 *   └─────────────────────────────────┘
 *
 * Por qué cada sección su propio header (y no uno compartido en MarketLayout):
 *  - Permite personalizar por sección (grocery puede tener color distinto,
 *    PromotionsBanner con animación distinta, etc.)
 *  - Header scrolea con el contenido — patrón mobile estándar (no pinned)
 *  - El floating fixed header (whiteMode on scroll-up) lo renderiza cada
 *    sección via portal para escapar el transform del slider
 *
 * Keep-alive:
 *  - Food siempre montada (sección default)
 *  - Grocery se monta la primera vez que el user togglea hacia ella
 *    (gating via groceryEverVisited). Después persiste en memoria por el
 *    resto de la sesión — no se re-monta al togglear.
 *
 * Trade-off: dos instancias de PromotionsBanner (Lottie player x2). El JSON
 * se descarga UNA sola vez (dynamic import cacheado). La animación de la
 * inactive corre offscreen — costo mínimo de CPU.
 */
export default function MarketLayout() {
  const location = useLocation();
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);

  const isHome = location.pathname === '/home' || location.pathname === '/';
  const isGrocery = location.pathname === '/grocery';

  // Lazy mount: grocery NO se monta hasta el primer toggle. Después persiste.
  // Si el user deep-linka a /grocery directo, se monta de entrada (isGrocery true).
  const [groceryEverVisited, setGroceryEverVisited] = useState(isGrocery);
  useEffect(() => {
    if (isGrocery && !groceryEverVisited) {
      setGroceryEverVisited(true);
    }
  }, [isGrocery, groceryEverVisited]);

  // Refs de scroll por sección — useStickyHeaderObservers escucha estos
  // containers en vez de window (cada sección tiene su scroll independiente).
  const foodScrollRef = useRef(null);
  const groceryScrollRef = useRef(null);

  // Scroll restoration: cuando el user navega de /home a /business/:slug y
  // vuelve con back, recupera el scroll donde lo dejó. Por sección.
  // (Entre /home ↔ /grocery el scroll ya se preserva naturalmente por el
  // keep-alive — esto cubre el caso de unmount completo del MarketLayout.)
  useScrollRestoration(foodScrollRef, 'marketplace-scroll:/home');
  useScrollRestoration(groceryScrollRef, 'marketplace-scroll:/grocery', groceryEverVisited);

  const activeIndex = isGrocery ? 1 : 0;

  return (
    <>
      {/* Slider area = full viewport. overflow-hidden recorta la sección
          offscreen durante el slide. */}
      <div className="fixed inset-0 overflow-hidden bg-white">
        <div
          className="flex h-full"
          style={{
            transform: `translateX(-${activeIndex * 100}%)`,
            transition: 'transform 400ms cubic-bezier(0.32, 0.72, 0, 1)',
          }}
        >
          {/* Food — siempre montada */}
          <div
            ref={foodScrollRef}
            className="flex-shrink-0 w-full h-full overflow-y-auto overflow-x-hidden"
          >
            <MarketSectionPage
              section="food"
              isActive={isHome}
              searchOverlayOpen={searchOverlayOpen}
              setSearchOverlayOpen={setSearchOverlayOpen}
              scrollContainerRef={foodScrollRef}
            />
          </div>

          {/* Grocery — mount diferido hasta primera visita, después persiste. */}
          <div
            ref={groceryScrollRef}
            className="flex-shrink-0 w-full h-full overflow-y-auto overflow-x-hidden"
          >
            {groceryEverVisited && (
              <MarketSectionPage
                section="grocery"
                isActive={isGrocery}
                searchOverlayOpen={searchOverlayOpen}
                setSearchOverlayOpen={setSearchOverlayOpen}
                scrollContainerRef={groceryScrollRef}
              />
            )}
          </div>
        </div>
      </div>

      {/* HomeCartSummary — UNA sola instancia compartida entre secciones
          (cart es estado global, no per-section). */}
      <HomeCartSummary />
    </>
  );
}
